import type { AnalyticsAdapter, AnalyticsEvent } from '../types';

declare global {
  interface Window {
    umami?: {
      track: (name: string, data?: Record<string, unknown>) => void;
    };
  }
}

/**
 * Placeholder adapter for Umami (self-hosted, Next.js + Postgres).
 * No-ops until UMAMI_URL and UMAMI_WEBSITE_ID are configured in the
 * environment (injected at runtime via window.__ANALYTICS_CONFIG__).
 */
export class UmamiAdapter implements AnalyticsAdapter {
  name = 'umami';
  requiredConsent = 'essential' as const;
  private scriptLoaded = false;

  constructor(
    private umamiUrl: string,
    private websiteId: string,
  ) {}

  load() {
    if (this.scriptLoaded || typeof window === 'undefined' || !this.umamiUrl) return;
    this.scriptLoaded = true;

    const script = document.createElement('script');
    script.src = this.umamiUrl;
    script.async = true;
    script.defer = true;
    script.dataset.websiteId = this.websiteId;
    document.head.appendChild(script);
  }

  track(event: AnalyticsEvent) {
    if (typeof window === 'undefined' || !window.umami) return;
    window.umami.track(event.name, {
      category: event.category,
      ...event.properties,
      client: event.context.client,
      platform: event.context.platform,
      screen: event.context.screenBucket,
    });
  }

  page() {
    // Umami auto-tracks page views via its script; no manual call needed
  }

  identify() {
    // Umami does not support user identification (privacy by design)
  }
}
